
import React, { memo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import TrendBadge from './trend-badge';
import SmaSlopeBadge from './sma-slope-badge';
import { formatPrice, formatMarketCap, formatVolume, formatPercentChange } from '../utils/format-helpers';
import { toggleWatchlist, isInWatchlist } from '../services/watchlist-service';
import './market-coin-card.css';

// Mobile counterpart to MarketTableRow — same data, stacked into a card so
// Weekly/Daily/4H/50 SMA are visible without horizontal scrolling.
function MarketCoinCard({ coin, onClick }) {
  const navigate = useNavigate();
  const [isInWatchlistState, setIsInWatchlistState] = useState(false);
  const [isWatchlistLoading, setIsWatchlistLoading] = useState(false);

  useEffect(() => {
    async function checkWatchlistStatus() {
      try {
        const inWatchlist = await isInWatchlist(coin.id);
        setIsInWatchlistState(inWatchlist);
      } catch (error) {
        console.error('Error checking watchlist status:', error);
      }
    }

    if (coin.id) {
      checkWatchlistStatus();
    }
  }, [coin.id]);

  const handleCardClick = (e) => {
    if (e.target.closest('.watchlist-btn')) {
      return;
    }
    if (onClick) {
      onClick(coin);
    } else {
      navigate('/coin/' + coin.id);
    }
  };

  const handleWatchlistToggle = async (e) => {
    e.stopPropagation();
    if (isWatchlistLoading) return;
    try {
      setIsWatchlistLoading(true);
      const newStatus = await toggleWatchlist(coin.id);
      setIsInWatchlistState(newStatus);
    } catch (error) {
      console.error('Error toggling watchlist:', error);
    } finally {
      setIsWatchlistLoading(false);
    }
  };

  const logoLetter = coin.symbol ? coin.symbol.charAt(0).toUpperCase() : '?';
  const isTaoSubnet = typeof coin.id === 'string' && coin.id.startsWith('subnet-');
  const currencySymbol = isTaoSubnet ? 'τ' : '$';
  const priceChangeClass = coin.price_change_percentage_24h >= 0 ? 'positive' : 'negative';

  const priceUp = coin.price_change_percentage_24h > 0;
  const isSpiking = coin.volumeRatio != null && coin.volumeRatio >= 1.5;
  const volumeClass = isSpiking
    ? (priceUp ? 'volume-spike-up' : 'volume-spike-down')
    : 'volume-neutral';

  const last10Volumes = coin.volumeHistory30d ? coin.volumeHistory30d.slice(-10) : [];
  const recentSpike = !coin.volumeSpike && coin.volumeAvg200 != null &&
    last10Volumes.some(v => v != null && v >= 2 * coin.volumeAvg200);

  return (
    <div className="market-coin-card" onClick={handleCardClick}>
      <div className="mcc-top-row">
        <button
          className={'watchlist-btn' + (isInWatchlistState ? ' active' : '') + (isWatchlistLoading ? ' loading' : '')}
          onClick={handleWatchlistToggle}
          disabled={isWatchlistLoading}
          aria-label={isInWatchlistState ? 'Remove from watchlist' : 'Add to watchlist'}
        >
          {isWatchlistLoading ? (
            <span className="watchlist-spinner">&#8987;</span>
          ) : isInWatchlistState ? (
            '★'
          ) : (
            '☆'
          )}
        </button>

        <span className="mcc-rank">#{coin.market_cap_rank || '-'}</span>

        <div className="mcc-coin-info">
          <div className="coin-logo">
            {coin.image ? (
              <img
                src={coin.image}
                alt={coin.name}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
            ) : null}
            <div className="coin-logo-fallback" style={{ display: coin.image ? 'none' : 'flex' }}>
              {logoLetter}
            </div>
          </div>
          <div className="mcc-coin-details">
            <div className="mcc-coin-name">{coin.name || 'Unknown'}</div>
            <div className="mcc-coin-symbol">{coin.symbol ? coin.symbol.toUpperCase() : 'N/A'}</div>
          </div>
        </div>

        <div className="mcc-price-block">
          <div className="mcc-price">{formatPrice(coin.current_price, currencySymbol)}</div>
          <div className={'mcc-change ' + priceChangeClass}>{formatPercentChange(coin.price_change_percentage_24h)}</div>
        </div>
      </div>

      <div className="mcc-stats-row">
        <span className="mcc-stat">Cap {formatMarketCap(coin.market_cap, currencySymbol)}</span>
        <span className={'mcc-stat mcc-volume ' + volumeClass}>
          {coin.volumeSpike && (
            <span className="volume-spike" title="Volume spike — more than 2x the 200-day average">&#9889;</span>
          )}
          {recentSpike && (
            <span className="volume-spike volume-spike-recent" title="Volume spiked 2x+ avg within the last 10 days (not today)">&#9889;</span>
          )}
          Vol {formatVolume(coin.volumeLatest ?? coin.total_volume, currencySymbol)}
          {coin.volumeRatio != null && <span className="mcc-volume-ratio">{coin.volumeRatio.toFixed(1)}x</span>}
        </span>
      </div>

      <div className="mcc-trend-row">
        <div className="mcc-trend-item">
          <span className="mcc-trend-label">Weekly</span>
          <TrendBadge trend={coin.trends ? coin.trends.weekly : coin.weeklyTrend} className="small" />
        </div>
        <div className="mcc-trend-item">
          <span className="mcc-trend-label">Daily</span>
          <TrendBadge trend={coin.trends ? coin.trends.daily : coin.dailyTrend} className="small" />
        </div>
        <div className="mcc-trend-item">
          <span className="mcc-trend-label">4H</span>
          <TrendBadge trend={coin.trends ? coin.trends.fourHour : coin.fourHourTrend} className="small" />
        </div>
      </div>

      <div className="mcc-sma-row">
        <span className="mcc-sma-label">50 SMA</span>
        <SmaSlopeBadge
          weekly={coin.indicators?.weekly?.sma50Slope}
          daily={coin.indicators?.daily?.sma50Slope}
          fourHour={coin.indicators?.fourHour?.sma50Slope}
        />
      </div>
    </div>
  );
}

export default memo(MarketCoinCard);
